import {useCallback, useEffect, useRef} from "react";
import {ToastContent} from "components";
import {toast} from "react-toastify";
import {useAppDispatch, useAppSelector} from "hooks/index";
import {activeLastCandidateAssessmentForProjectSelector} from "store/background-tasks/backgroundTaskSelectors";
import {
	STBackgroundTaskNotificationPayload,
	STBackgroundTaskResponse,
	TBackgroundTaskStatus,
} from "api/backgroundTaskApi/types";
import {clearProjectCandidateAssessmentProcess} from "store/background-tasks/backgroundTaskActions";
import {subscribeToChannel, unsubscribeFromChannel} from "web-socket/pusherClient";
import {IAssessmentCache, TProjectCandidateAssessmentNotificationData} from "./types";
import {userPusherChannelSelector} from "store/user/userSelectors";
import {updateProjectCandidateByKeyInTheList} from "store/project/projectActions";
import {useHistory} from "react-router-dom";
import {setAPFilter} from "store/additional-profiles/additionalProfilesActions";
import {
	checkCandidateAssessmentStatus,
	sortProjectCandidatesListByScore,
} from "store/project/Assessment/projectAssessmentAsyncActions";

// Constants
const POLLING_INTERVAL = 60 * 1000; // 60 seconds
const FALLBACK_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const MAX_POLLING_TIME = 30 * 60 * 1000; // 30 minutes

const useProjectCandidateScoringProgressWatcher = () => {
	const dispatch = useAppDispatch();
	const history = useHistory();
	const channelName = useAppSelector(userPusherChannelSelector);
	const activeAssessment: STBackgroundTaskResponse = useAppSelector(
		activeLastCandidateAssessmentForProjectSelector,
	);
	// Refs for state management
	const statusRef = useRef<{isActive: boolean}>({isActive: true});
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const fallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lastActivityRef = useRef<number>(Date.now());
	const assessmentCacheRef = useRef<IAssessmentCache>({});
	const subscribedChannelsRef = useRef<Set<string>>(new Set());

	// Cleanup helper
	const cleanup = useCallback(() => {
		statusRef.current.isActive = false;

		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}

		if (fallbackTimeoutRef.current) {
			clearTimeout(fallbackTimeoutRef.current);
			fallbackTimeoutRef.current = null;
		}

		subscribedChannelsRef.current.forEach((channel) => {
			unsubscribeFromChannel(channel, "Candidate Assessed");
			unsubscribeFromChannel(channel, "Background Task Status Change");
		});
		subscribedChannelsRef.current.clear();

		assessmentCacheRef.current = {};
	}, []);

	// Check if the assessment is still in progress
	const isAssessmentInProgress = useCallback(() => {
		return (
			activeAssessment?.id &&
			activeAssessment.type === "Candidate Assessment" &&
			["Created", "In Progress"].includes(activeAssessment.status)
		);
	}, [activeAssessment]);

	// Notification handler
	const displayNotification = useCallback(
		(projectId: number, status: TBackgroundTaskStatus) => {
			if (!statusRef.current.isActive) return;

			if (status === "Completed") {
				dispatch(clearProjectCandidateAssessmentProcess());
				dispatch(setAPFilter({attribute: "ordering", value: "-fit_to_brief_score"}));

				toast.success(ToastContent, {
					toastId: `assessment-${projectId}`,
					autoClose: false,
					closeOnClick: false,
					data: {
						title: "Candidate Assessment for project is ready",
						description: (closeToast: () => void, linkColor: string) => (
							<>
								You can check the report by{" "}
								<span
									className="toast-link"
									style={{color: linkColor}}
									onClick={() => {
										history.push(`/project/${projectId}`);
										closeToast();
									}}
								>
									clicking here.
								</span>
							</>
						),
					},
				});
			} else if (status === "Error") {
				toast.info(ToastContent, {
					toastId: `assessment-error-${projectId}`,
					autoClose: false,
					closeOnClick: true,
					data: {
						title: "Something went wrong while assessing candidates!",
					},
				});
				dispatch(clearProjectCandidateAssessmentProcess());
			}
		},
		[dispatch, history],
	);

	// Check assessment status
	const checkStatus = useCallback(async () => {
		if (!activeAssessment?.id || !statusRef.current.isActive) return;

		try {
			const response = await dispatch(checkCandidateAssessmentStatus(activeAssessment.id));

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			if (response?.status === "Completed" || response?.status === "Error") {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				displayNotification(activeAssessment.project_id, response.status);
				cleanup();

				return true;
			}

			return false;
		} catch (error) {
			console.error("Error checking assessment status:", error);

			return false;
		}
	}, [activeAssessment, dispatch, displayNotification, cleanup]);

	// Start polling as fallback
	const startPolling = useCallback(async () => {
		if (intervalRef.current) return; // Already polling

		// Check the current status before starting polling
		const isCompleted = await checkStatus();
		if (isCompleted) return;

		// Only start polling if the assessment is still in progress
		if (!isAssessmentInProgress()) {
			cleanup();

			return;
		}

		console.warn("Starting fallback polling mechanism due to inactivity");
		intervalRef.current = setInterval(async () => {
			if (!statusRef.current.isActive || !isAssessmentInProgress()) {
				cleanup();

				return;
			}

			if (Date.now() - lastActivityRef.current > MAX_POLLING_TIME) {
				console.warn("Maximum polling time exceeded");
				cleanup();

				return;
			}

			await checkStatus();
		}, POLLING_INTERVAL);
	}, [isAssessmentInProgress, checkStatus, cleanup]);

	// Setup fallback mechanism
	const setupFallback = useCallback(() => {
		if (fallbackTimeoutRef.current) {
			clearTimeout(fallbackTimeoutRef.current);
		}

		fallbackTimeoutRef.current = setTimeout(async () => {
			if (isAssessmentInProgress()) {
				await startPolling();
			}
		}, FALLBACK_TIMEOUT);
	}, [isAssessmentInProgress, startPolling]);

	// Handle candidate assessment updates via Pusher
	const handleCandidateAssessed = useCallback(
		(data: TProjectCandidateAssessmentNotificationData) => {
			if (!statusRef.current.isActive || !activeAssessment) return;

			// Update the last activity timestamp and reset fallback
			lastActivityRef.current = Date.now();
			setupFallback();

			const cacheKey = `${data.candidate_id}-${data.background_task_id}`;
			const cachedResult = assessmentCacheRef.current[cacheKey];

			// Skip if already processed
			if (
				cachedResult &&
				cachedResult.score === data.score &&
				JSON.stringify(cachedResult.assessments) === JSON.stringify(data.assessments)
			) {
				return;
			}

			if (data.background_task_id === activeAssessment.id) {
				assessmentCacheRef.current[cacheKey] = {
					score: data.score,
					assessments: data.assessments,
					timestamp: Date.now(),
				};

				dispatch(
					updateProjectCandidateByKeyInTheList({
						candidate_id: data.candidate_id,
						is_assessed: true,
						key: "fit_to_brief",
						value: {
							score: data.score,
							assessments: data.assessments,
						},
					}),
				);
			}
		},
		[activeAssessment, dispatch, setupFallback],
	);

	// Handle background task status changes via Pusher
	const handleBackgroundTaskStatus = useCallback(
		(data: STBackgroundTaskNotificationPayload) => {
			if (!statusRef.current.isActive || !activeAssessment) return;

			// Update the last activity timestamp and reset fallback
			lastActivityRef.current = Date.now();
			setupFallback();

			if (
				activeAssessment.id === data.background_task_id &&
				(data.status === "Completed" || data.status === "Error") &&
				data.source === "dataflow.additional_profiles.services.notifications"
			) {
				displayNotification(activeAssessment.project_id, data.status);
				dispatch(clearProjectCandidateAssessmentProcess());
				dispatch(sortProjectCandidatesListByScore());
				cleanup();
			}
		},
		[activeAssessment, dispatch, cleanup, displayNotification, setupFallback],
	);

	// Setup effect
	useEffect(() => {
		statusRef.current.isActive = true;
		lastActivityRef.current = Date.now();

		if (isAssessmentInProgress()) {
			// Setup Pusher subscriptions
			if (channelName) {
				subscribedChannelsRef.current.add(channelName);

				subscribeToChannel<TProjectCandidateAssessmentNotificationData>(
					channelName,
					"Candidate Assessed",
					handleCandidateAssessed,
				);

				subscribeToChannel<STBackgroundTaskNotificationPayload>(
					channelName,
					"Background Task Status Change",
					handleBackgroundTaskStatus,
				);

				// Initial setup of fallback
				setupFallback();
			}
		}

		return cleanup;
	}, [
		channelName,
		handleCandidateAssessed,
		handleBackgroundTaskStatus,
		setupFallback,
		cleanup,
		isAssessmentInProgress,
	]);
};
export default useProjectCandidateScoringProgressWatcher;
